import { useEffect, useMemo, useState } from 'react';
import {
  Package,
  Settings2,
  Droplets,
  Scale,
  UtensilsCrossed,
  LayoutGrid,
  Users,
  UserCog,
  ShoppingBag,
  SlidersHorizontal,
  Copy,
  ChevronDown,
  ChevronUp,
  Search,
  ArrowLeft,
  CheckCircle2,
  Lightbulb,
} from 'lucide-react';
import Modal from '../ui/Modal';
import apiService from '../../services/api';
import {
  ARCHETYPE_IDS,
  ARCHETYPE_LIST,
  getArchetypeById,
  getHighlightedArchetypeIds,
  shouldSkipScreen2,
} from '../../config/produitArchetypes.js';
import { deduceProduitArchetype } from '../../utils/deduceProduitArchetype.js';
import {
  buildDefaultScreen2Answers,
  getFormuleStepPreview,
  getStepAfterArchetypeSelect,
} from '../../utils/produitAssistantFlow.js';
import {
  getHiddenSectionLabels,
  getVisibleSectionLabels,
} from '../../utils/produitSectionVisibility.js';

const ARCHETYPE_ICONS = {
  [ARCHETYPE_IDS.SIMPLE]: Package,
  [ARCHETYPE_IDS.AVEC_OPTIONS]: Settings2,
  [ARCHETYPE_IDS.SAUCES]: Droplets,
  [ARCHETYPE_IDS.POIDS]: Scale,
  [ARCHETYPE_IDS.FORMULE]: UtensilsCrossed,
  [ARCHETYPE_IDS.PLATEAU_PIECES]: LayoutGrid,
  [ARCHETYPE_IDS.PLANCHE_PERSONNES]: Users,
  [ARCHETYPE_IDS.PLANCHE_PERSO]: UserCog,
  [ARCHETYPE_IDS.INGREDIENT_PLANCHE]: ShoppingBag,
  [ARCHETYPE_IDS.RETAIL]: ShoppingBag,
  [ARCHETYPE_IDS.AVANCE]: SlidersHorizontal,
};

/**
 * Assistant création produit — Lot L-UX-1 (écrans 1–3).
 *
 * @param {(payload: {
 *   archetypeId: string,
 *   answers: Record<string, unknown>,
 *   sourceProductId?: string,
 *   mode: 'new'|'copy'|'expert'
 * }) => void} props.onComplete
 */
export default function ProduitAssistantWizard({
  isOpen,
  onClose,
  commercantId,
  produits = [],
  onComplete,
}) {
  const [step, setStep] = useState(1);
  const [commercantCategorie, setCommercantCategorie] = useState(null);
  const [selectedArchetypeId, setSelectedArchetypeId] = useState(null);
  const [completionMode, setCompletionMode] = useState('new');
  const [screen2Answers, setScreen2Answers] = useState({});
  const [showOtherModels, setShowOtherModels] = useState(false);
  const [showCopyPanel, setShowCopyPanel] = useState(false);
  const [copySearch, setCopySearch] = useState('');
  const [sourceProductId, setSourceProductId] = useState(null);

  useEffect(() => {
    if (!isOpen || !commercantId) return;

    let cancelled = false;
    (async () => {
      try {
        const data = await apiService.getCommercant(commercantId);
        const commercant = data?.commercant ?? data;
        if (!cancelled) {
          setCommercantCategorie(commercant?.categorie ?? null);
        }
      } catch {
        if (!cancelled) setCommercantCategorie(null);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, commercantId]);

  useEffect(() => {
    if (!isOpen) {
      setStep(1);
      setSelectedArchetypeId(null);
      setCompletionMode('new');
      setScreen2Answers({});
      setShowOtherModels(false);
      setShowCopyPanel(false);
      setCopySearch('');
      setSourceProductId(null);
    }
  }, [isOpen]);

  const highlightIds = useMemo(
    () => getHighlightedArchetypeIds(commercantCategorie),
    [commercantCategorie]
  );

  const { primaryArchetypes, otherArchetypes } = useMemo(() => {
    const primary = highlightIds
      .map((id) => getArchetypeById(id))
      .filter(Boolean);
    const primarySet = new Set(highlightIds);
    const other = ARCHETYPE_LIST.filter((a) => !primarySet.has(a.id));
    return { primaryArchetypes: primary, otherArchetypes: other };
  }, [highlightIds]);

  const copyCandidates = useMemo(() => {
    const q = copySearch.trim().toLowerCase();
    if (!q) return produits.slice(0, 12);
    return produits
      .filter(
        (p) =>
          p.nom?.toLowerCase().includes(q) ||
          p.categorie?.toLowerCase().includes(q)
      )
      .slice(0, 12);
  }, [produits, copySearch]);

  const sourceProduct = sourceProductId
    ? produits.find((p) => p.id === sourceProductId)
    : null;

  const deducedFromCopy = sourceProduct
    ? deduceProduitArchetype(sourceProduct)
    : null;

  const effectiveArchetypeId =
    selectedArchetypeId ||
    (showCopyPanel && deducedFromCopy ? deducedFromCopy.archetypeId : null);

  const activeArchetype = effectiveArchetypeId
    ? getArchetypeById(effectiveArchetypeId)
    : null;

  const goToStepAfterSelect = (archetypeId, mode) => {
    const archetype = getArchetypeById(archetypeId);
    setSelectedArchetypeId(archetypeId);
    setCompletionMode(mode);
    setScreen2Answers(buildDefaultScreen2Answers(archetype?.screen2Questions));
    setStep(getStepAfterArchetypeSelect(archetypeId));
  };

  const handleScreen1Continue = () => {
    if (!effectiveArchetypeId) return;
    const mode = showCopyPanel && sourceProductId ? 'copy' : 'new';
    goToStepAfterSelect(effectiveArchetypeId, mode);
  };

  const handleExpert = () => {
    goToStepAfterSelect(ARCHETYPE_IDS.AVANCE, 'expert');
  };

  const handleScreen2Continue = () => {
    if (!effectiveArchetypeId) return;
    setStep(3);
  };

  const handleOpenFiche = () => {
    if (!effectiveArchetypeId) return;
    onComplete({
      archetypeId: effectiveArchetypeId,
      answers: { ...screen2Answers },
      sourceProductId: sourceProductId || undefined,
      mode: completionMode,
    });
  };

  const handleBack = () => {
    if (step === 3) {
      setStep(shouldSkipScreen2(effectiveArchetypeId) ? 1 : 2);
      return;
    }
    if (step === 2) {
      setStep(1);
    }
  };

  const setAnswer = (questionId, value) => {
    setScreen2Answers((prev) => ({ ...prev, [questionId]: value }));
  };

  const renderCard = (archetype) => {
    const Icon = ARCHETYPE_ICONS[archetype.id] ?? Package;
    const isSelected = effectiveArchetypeId === archetype.id && step === 1;

    return (
      <button
        key={archetype.id}
        type="button"
        onClick={() => {
          setSelectedArchetypeId(archetype.id);
          setShowCopyPanel(false);
          setSourceProductId(null);
        }}
        className={`text-left rounded-xl border p-4 transition-all hover:shadow-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
          isSelected
            ? 'border-blue-600 bg-blue-50 ring-1 ring-blue-600'
            : 'border-gray-200 bg-white hover:border-gray-300'
        }`}
      >
        <div className="flex items-start gap-3">
          <div
            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${
              isSelected ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600'
            }`}
          >
            <Icon className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <p className="font-semibold text-gray-900 text-sm">{archetype.label}</p>
            <p className="text-xs text-gray-500 mt-1 leading-relaxed">{archetype.subtitle}</p>
          </div>
        </div>
      </button>
    );
  };

  const renderQuestion = (question) => {
    const value = screen2Answers[question.id];

    if (question.type === 'boolean') {
      return (
        <div key={question.id} className="flex items-center justify-between gap-4 py-2">
          <span className="text-sm text-gray-700">{question.label}</span>
          <div className="flex gap-2">
            {[
              { v: true, label: 'Oui' },
              { v: false, label: 'Non' },
            ].map(({ v, label }) => (
              <button
                key={label}
                type="button"
                onClick={() => setAnswer(question.id, v)}
                className={`px-3 py-1.5 text-sm rounded-md border ${
                  value === v
                    ? 'border-blue-600 bg-blue-50 text-blue-800 font-medium'
                    : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      );
    }

    if (question.type === 'number') {
      return (
        <div key={question.id} className="space-y-2">
          <label className="text-sm font-medium text-gray-700">{question.label}</label>
          <input
            type="number"
            min={question.min}
            max={question.max}
            value={value ?? ''}
            onChange={(e) => setAnswer(question.id, Number(e.target.value))}
            className="w-full max-w-xs rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
      );
    }

    return (
      <div key={question.id} className="space-y-2">
        <p className="text-sm font-medium text-gray-700">{question.label}</p>
        <div className="space-y-2">
          {(question.choices ?? []).map((choice) => (
            <label
              key={choice.value}
              className={`flex items-center gap-3 rounded-lg border px-3 py-2.5 cursor-pointer ${
                value === choice.value
                  ? 'border-blue-600 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <input
                type="radio"
                name={question.id}
                checked={value === choice.value}
                onChange={() => setAnswer(question.id, choice.value)}
                className="text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-800">{choice.label}</span>
            </label>
          ))}
        </div>
      </div>
    );
  };

  const renderScreen1 = () => (
    <>
      <div>
        <h3 className="text-base font-semibold text-gray-900">
          Quel type de produit créez-vous ?
        </h3>
        <p className="text-sm text-gray-500 mt-1">
          Seules les sections utiles seront affichées dans la fiche produit.
        </p>
      </div>

      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-3">
          Les plus courants
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {primaryArchetypes.map(renderCard)}
        </div>
      </div>

      <div>
        <button
          type="button"
          onClick={() => setShowOtherModels((v) => !v)}
          className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-gray-900"
        >
          {showOtherModels ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          Autres modèles
        </button>
        {showOtherModels && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-3">
            {otherArchetypes.map(renderCard)}
          </div>
        )}
      </div>

      <div className="border-t border-gray-100 pt-4 space-y-3">
        <button
          type="button"
          onClick={() => {
            setShowCopyPanel((v) => !v);
            setSelectedArchetypeId(null);
          }}
          className={`flex items-center gap-2 text-sm font-medium ${
            showCopyPanel ? 'text-blue-700' : 'text-gray-700 hover:text-gray-900'
          }`}
        >
          <Copy className="h-4 w-4" />
          Reprendre un produit existant comme modèle
        </button>

        {showCopyPanel && (
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={copySearch}
                onChange={(e) => setCopySearch(e.target.value)}
                placeholder="Rechercher un produit…"
                className="w-full rounded-md border border-gray-300 py-2 pl-10 pr-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <div className="max-h-40 overflow-y-auto space-y-1">
              {copyCandidates.length === 0 ? (
                <p className="text-sm text-gray-500 py-2">Aucun produit trouvé.</p>
              ) : (
                copyCandidates.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => setSourceProductId(p.id)}
                    className={`w-full text-left rounded-md px-3 py-2 text-sm ${
                      sourceProductId === p.id
                        ? 'bg-blue-100 text-blue-900 font-medium'
                        : 'hover:bg-white text-gray-700'
                    }`}
                  >
                    {p.nom}
                    {p.categorie ? (
                      <span className="text-gray-400 font-normal"> — {p.categorie}</span>
                    ) : null}
                  </button>
                ))
              )}
            </div>
            {deducedFromCopy && sourceProduct && (
              <p className="text-sm text-gray-600">
                Modèle détecté :{' '}
                <span className="font-medium text-gray-900">
                  {getArchetypeById(deducedFromCopy.archetypeId)?.label ??
                    deducedFromCopy.archetypeId}
                </span>
              </p>
            )}
          </div>
        )}

        <button
          type="button"
          onClick={handleExpert}
          className="text-sm text-gray-500 hover:text-gray-800 underline-offset-2 hover:underline"
        >
          Mode expert — toutes les sections
        </button>
      </div>
    </>
  );

  const renderScreen2 = () => (
    <>
      <div>
        <p className="text-xs font-medium text-blue-600 uppercase tracking-wide">
          {activeArchetype?.label}
        </p>
        <h3 className="text-base font-semibold text-gray-900 mt-1">Quelques précisions</h3>
        <p className="text-sm text-gray-500 mt-1">
          Ces réponses préparent la fiche produit (étape suivante).
        </p>
      </div>
      <div className="space-y-5">
        {(activeArchetype?.screen2Questions ?? []).map(renderQuestion)}
      </div>
    </>
  );

  const renderScreen3 = () => {
    const visibleLabels = getVisibleSectionLabels(effectiveArchetypeId);
    const hiddenLabels = getHiddenSectionLabels(effectiveArchetypeId);
    const pilot = activeArchetype?.pilotExample;
    const formuleSteps =
      effectiveArchetypeId === ARCHETYPE_IDS.FORMULE
        ? getFormuleStepPreview(screen2Answers)
        : null;

    return (
      <>
        <div>
          <p className="text-xs font-medium text-blue-600 uppercase tracking-wide">
            Récapitulatif
          </p>
          <h3 className="text-base font-semibold text-gray-900 mt-1">
            {activeArchetype?.label}
          </h3>
          <p className="text-sm text-gray-500 mt-1">{activeArchetype?.subtitle}</p>
        </div>

        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-3">
          <p className="text-sm font-medium text-gray-800">Vous allez renseigner :</p>
          <ul className="space-y-1.5">
            {visibleLabels.map((label) => (
              <li key={label} className="flex items-start gap-2 text-sm text-gray-700">
                <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0 mt-0.5" />
                {label}
              </li>
            ))}
          </ul>
          {formuleSteps && (
            <p className="text-sm text-gray-600 pt-1">
              Étapes formule : {formuleSteps.join(' → ')}
            </p>
          )}
          {hiddenLabels.length > 0 && effectiveArchetypeId !== ARCHETYPE_IDS.AVANCE && (
            <p className="text-xs text-gray-500 pt-2 border-t border-gray-200">
              Masqué pour ce modèle : {hiddenLabels.slice(0, 5).join(', ')}
              {hiddenLabels.length > 5 ? '…' : ''} (ajoutable via Paramètres avancés à l&apos;étape
              4)
            </p>
          )}
        </div>

        {pilot?.commercant && pilot.commercant !== '—' && (
          <div className="flex gap-3 rounded-lg border border-amber-100 bg-amber-50/80 p-4">
            <Lightbulb className="h-5 w-5 text-amber-600 shrink-0" />
            <div className="text-sm text-amber-900">
              <p className="font-medium">Exemple</p>
              <p className="mt-0.5">
                {pilot.commercant} — « {pilot.product} »
              </p>
            </div>
          </div>
        )}

        <p className="text-xs text-gray-500">
          La fiche produit complète s&apos;ouvrira ensuite (filtrage des sections à l&apos;étape
          4).
        </p>
      </>
    );
  };

  const stepTitle =
    step === 1
      ? 'Nouveau produit — choix du modèle'
      : step === 2
        ? 'Nouveau produit — précisions'
        : 'Nouveau produit — récapitulatif';

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={stepTitle} maxWidth="max-w-4xl">
      <div className="px-6 pb-6 space-y-6">
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <span className={step === 1 ? 'font-semibold text-blue-600' : ''}>1. Modèle</span>
          <span>›</span>
          <span
            className={
              step === 2 ? 'font-semibold text-blue-600' : shouldSkipScreen2(effectiveArchetypeId) && step > 1 ? 'text-gray-300' : ''
            }
          >
            2. Précisions
          </span>
          <span>›</span>
          <span className={step === 3 ? 'font-semibold text-blue-600' : ''}>3. Récap</span>
        </div>

        {step === 1 && renderScreen1()}
        {step === 2 && renderScreen2()}
        {step === 3 && renderScreen3()}

        <div className="flex justify-between gap-3 pt-2 border-t border-gray-100">
          <div>
            {step > 1 && (
              <button
                type="button"
                onClick={handleBack}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
              >
                <ArrowLeft className="h-4 w-4" />
                Retour
              </button>
            )}
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Annuler
            </button>
            {step === 1 && (
              <button
                type="button"
                disabled={!effectiveArchetypeId}
                onClick={handleScreen1Continue}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Continuer
              </button>
            )}
            {step === 2 && (
              <button
                type="button"
                onClick={handleScreen2Continue}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
              >
                Continuer
              </button>
            )}
            {step === 3 && (
              <button
                type="button"
                onClick={handleOpenFiche}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
              >
                Ouvrir la fiche produit
              </button>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};
